import { fetchAPI } from "./api";
import { serviceErrorResult } from "./service-utils";

/**
 * Interface for Tree Availability Request
 */
export interface TreeAvailabilityRequest {
    dep_type: "PROJECT" | "SPECIES" | "ATTRIBUTE";
    dep_id?: number;
    is_geotagged?: boolean;
}

/**
 * Interface for Species Breakdown (when DEP = PROJECT)
 */
export interface SpeciesBreakdown {
    species_id: number;
    available: number;
}

/**
 * Interface for Project Breakdown (when DEP = SPECIES)
 */
export interface ProjectBreakdown {
    project_id: number;
    available: number;
}

/**
 * Interface for Tree Availability Response
 */
export interface TreeAvailabilityResponse {
    success: boolean;
    dep_type: "PROJECT" | "SPECIES" | "ATTRIBUTE";
    project_id?: number;
    species_id?: number;
    total_capacity?: number;
    total_available: number;
    total_available_combined?: number;
    total_reserved?: number;
    species_breakdown?: SpeciesBreakdown[];
    project_breakdown?: ProjectBreakdown[];
    message?: string;
}

/**
 * Fetch available trees based on DEP type
 * GET /api/allocations/availability/project/<dep_id>/?is_geotagged=false
 * GET /api/allocations/availability/species/<dep_id>/?is_geotagged=true
 * GET /api/allocations/availability/attribute/?is_geotagged=false
 */
export async function fetchTreeAvailability(
    request: TreeAvailabilityRequest
): Promise<TreeAvailabilityResponse> {
    try {
        let endpoint: string;

        if (request.dep_type === "PROJECT" && request.dep_id !== undefined) {
            endpoint = `/allocations/availability/project/${request.dep_id}/`;
        } else if (request.dep_type === "SPECIES" && request.dep_id !== undefined) {
            endpoint = `/allocations/availability/species/${request.dep_id}/`;
        } else {
            endpoint = `/allocations/availability/attribute/`;
        }

        const data = await fetchAPI<TreeAvailabilityResponse>(
            endpoint,
            { is_geotagged: request.is_geotagged ?? false }
        );
        return data;
    } catch (error) {
        return serviceErrorResult("Error fetching tree availability:", error, {
            success: false,
            dep_type: request.dep_type,
            total_available: 0,
            message: "Unable to fetch live tree availability right now.",
        });
    }
}

